import React, { useEffect, useState, useRef } from 'react';
import { useGroupMembers, preloadGroupMembers } from '~/src/api_hooks/groups';
import { useWhoAmI } from '~/src/api_hooks/users';
import { Post as PostData, Comment } from '~/src/types/posts';
import { Uuid, UuidString } from '~src/utils/UUID';
import { usePeepStore } from '~/src/state';
import { peep_client } from '~/src/api/peep';
import { CommentMention } from '~/src/api/peep/comments';
import { useAbort } from '~/src/utils/useAbort';
import { add as dateAdd, isPast, formatDistanceToNowStrict, compareAsc } from 'date-fns';
import { produce } from 'immer';
import CommentInput from './CommentInput';
import MediaCarousel from './MediaCarousel';
import MediaViewer from './MediaViewer';
import { Ellipsis, Trash2 } from 'lucide-react';
import { Menu } from '@mantine/core';
import { ReactionsDisplay } from './ReactionComponents';
import { ReactionType } from '~/src/types/posts';

const profile_image_size = '40px';

export default function Post({
  initialData,
  onDelete,
}: {
  initialData: PostData;
  onDelete?: (post_id: UuidString) => void;
}) {
  const [selectedGroup, setSelectedGroup] = useState<UuidString>(initialData.groups[0]);
  const [comments, setComments] = useState(initialData.comments);
  const [mediaViewerOpened, setMediaViewerOpened] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [postReactions, setPostReactions] = useState(initialData.reactions);
  const { data: myId } = useWhoAmI();

  const addComments = (comments_to_add: Comment[], group_id: UuidString) => {
    setComments(
      produce((previous) => {
        const group_comments = previous?.get(group_id);
        if (group_comments) {
          previous?.set(group_id, [...group_comments, ...comments_to_add]);
        } else {
          previous?.set(group_id, comments_to_add);
        }
      }),
    );
  };

  const deleteComment = async (comment_id: UuidString, group_id: UuidString) => {
    try {
      await peep_client.comments.delete_comment(comment_id);
      setComments(
        produce((previous) => {
          const group_comments = previous?.get(group_id);
          if (group_comments) {
            const comment_index = group_comments?.findIndex((comment) => comment.id === comment_id);
            if (comment_index !== undefined && comment_index !== -1) {
              group_comments.splice(comment_index, 1);
              previous?.set(group_id, [...group_comments]);
            }
          }
        }),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const updateCommentReactions = (comment_id: UuidString, group_id: UuidString, type: ReactionType, action: 'add' | 'remove', user_id: UuidString) => {
    setComments(produce(draft => {
      const group_comments = draft?.get(group_id);
      if (group_comments) {
        const comment = group_comments.find(c => c.id === comment_id);
        if (comment) {
          const set = comment.reactions.get(type) || new Set();
          if (action === 'add') {
            set.add(user_id);
            comment.reactions.set(type, set);
          } else {
            set.delete(user_id);
            if (set.size === 0) {
              comment.reactions.delete(type);
            } else {
              comment.reactions.set(type, set);
            }
          }
        }
      }
    }));
  };

  const handleMediaClick = (mediaIndex: number) => {
    setSelectedMediaIndex(mediaIndex);
    setMediaViewerOpened(true);
  };

  return (
    <div
      className={'Post'}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '0px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #f0f0f0',
        marginBottom: '1rem',
      }}
    >
      <div
        className="post-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          paddingTop: '1rem',
          paddingLeft: '1rem',
          paddingRight: '1rem',
        }}
      >
        <div
          className="photo-mask"
          style={{
            overflow: 'hidden',
            height: profile_image_size,
            width: profile_image_size,
            borderRadius: '50%',
            marginRight: '1rem',
            flexShrink: 0,
          }}
        >
          <img
            style={{
              height: profile_image_size,
              width: profile_image_size,
              transform: 'scale(1.5)',
              backgroundColor: 'black',
            }}
          />
        </div>
        <div
          className="poster-ctx"
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Poster user_id={initialData.posted_by} relative_to_group_id={selectedGroup} />
            {initialData.posted_by && myId === initialData.posted_by && onDelete && (
              <PostActions post_id={initialData.id} deletePost={onDelete} />
            )}
          </div>
          <GroupCtx selected_group={selectedGroup} all_groups={initialData.groups} />
        </div>
      </div>

      <div
        className="text-content"
        style={{ margin: '0.8rem 1rem 1rem 1rem', fontSize: '11pt', lineHeight: '1.4', color: '#333' }}
      >
        {initialData.text_content}
      </div>

      {initialData.media.length > 0 && <MediaCarousel media={initialData.media} onMediaClick={handleMediaClick} />}


      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '1rem', paddingRight: '1rem', marginBottom: '0.5rem' }}>
        <ReactionsDisplay
          groupId={selectedGroup}
          reactions={postReactions}
          currentUserId={myId}
          onAddReaction={async (type) => {
            // Optimistic update
            setPostReactions(produce(draft => {
              const set = draft.get(type) || new Set();
              if (myId) set.add(myId);
              draft.set(type, set);
            }));
            await peep_client.posts.add_reaction(initialData.id, type);
          }}
          onRemoveReaction={async (type) => {
            // Optimistic update
            setPostReactions(produce(draft => {
              const set = draft.get(type);
              if (set && myId) {
                set.delete(myId);
                if (set.size === 0) {
                  draft.delete(type);
                } else {
                  draft.set(type, set);
                }
              }
            }));
            await peep_client.posts.remove_reaction(initialData.id);
          }}
        />
        <RelativePostDate postDate={initialData.posted_at} />
      </div>

      <MentionedUsers
        relative_to_group_id={selectedGroup}
        user_ids={initialData.mentioned_users}
        preload_groups={initialData.groups}
      />
      <GroupPicker
        selected_group={selectedGroup}
        all_groups={initialData.groups}
        onSelect={(newly_selected_group) => setSelectedGroup(newly_selected_group)}
      />

      <Comments
        initialComments={comments}
        selectedGroup={selectedGroup}
        postId={initialData.id}
        addComments={addComments}
        deleteComment={deleteComment}
        addReactionToComment={async (comment_id, type, group_id) => {
          if (myId) {
            updateCommentReactions(comment_id, group_id, type, 'add', myId);
            await peep_client.comments.add_reaction(comment_id, type);
          }
        }}
        removeReactionFromComment={async (comment_id, type, group_id) => {
          if (myId) {
            updateCommentReactions(comment_id, group_id, type, 'remove', myId);
            await peep_client.comments.remove_reaction(comment_id);
          }
        }}
      />

      <MediaViewer
        media={initialData.media}
        initialIndex={selectedMediaIndex}
        opened={mediaViewerOpened}
        onClose={() => setMediaViewerOpened(false)}
      />
    </div>
  );
}

function RelativePostDate({ postDate }: { postDate: Date }) {
  const [relativePostDate, setRelativePostDate] = useState(formatDistanceToNowStrict(postDate, { addSuffix: true }));

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRelativePostDate(formatDistanceToNowStrict(postDate, { addSuffix: true }));
    }, 15_000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <span style={{ fontFamily: 'Lato', color: '#999', fontSize: '9pt' }} title={postDate.toLocaleString()}>
      {relativePostDate}
    </span>
  );
}

function Comments({
  initialComments,
  selectedGroup,
  postId,
  addComments,
  deleteComment,
  addReactionToComment,
  removeReactionFromComment,
}: {
  initialComments: PostData['comments'];
  selectedGroup: UuidString;
  postId: UuidString;
  addComments: (comments_to_add: Comment[], group_id: UuidString) => void;
  deleteComment: (comment_id: UuidString, group_id: UuidString) => void;
  addReactionToComment: (comment_id: UuidString, type: ReactionType, group_id: UuidString) => void;
  removeReactionFromComment: (comment_id: UuidString, type: ReactionType, group_id: UuidString) => void;
}) {
  const [getCommentsButtonEnabled, setGetCommentsButtonEnabled] = useState(true);
  const { data: user_id, isLoading } = useWhoAmI();
  const scrollableArea = useRef<HTMLDivElement | null>(null);

  const selectedComments = [...(initialComments?.get(selectedGroup) || [])];
  selectedComments.sort((l, r) => compareAsc(l.inserted_at, r.inserted_at));

  useEffect(() => {
    scrollToBottom();
  }, []);

  // This makes sure we start at the bottom of the comments
  // without having to reverse the comment array
  const scrollToBottom = () => {
    if (scrollableArea.current) {
      scrollableArea.current.scrollTop = scrollableArea.current.scrollHeight;
    }
  };

  const postComment = async (text_content: string, mentions: CommentMention[] = []) => {
    const comment_id = await peep_client.comments.post_comment({
      text_content,
      mentioned_users: mentions,
      post_id: postId,
      group_id: selectedGroup,
    });

    addComments(
      [
        {
          id: comment_id,
          text_content,
          group_id: selectedGroup,
          post_id: postId,
          commented_by_user_id: await peep_client.users.whoami(),
          inserted_at: new Date(),
          mentions: mentions.map((mention) => ({
            comment_id: comment_id,
            character_index: mention.character_index,
            mentioned_user_id: mention.mentioned_user_id,
            inserted_at: new Date(),
          })),
          reactions: new Map(),
        },
      ],
      selectedGroup,
    );

    // Scroll to bottom to show the new comment
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  return (
    <div
      className={'Comments'}
      style={{
        display: 'flex',
        flex: '1 1 0%',
        flexDirection: 'column',
        marginTop: '0.8rem',
        marginRight: '1rem',
        marginLeft: '1rem',
        marginBottom: '1rem',
        backgroundColor: '#f8f9fa',
        padding: '0.8rem',
        borderRadius: '12px',
        fontSize: '9pt',
        gap: '0.6rem',
        maxHeight: '15rem',
        border: '1px solid #e9ecef',
      }}
    >
      <div
        style={{
          display: 'flex',
          flex: '1 1 0%',
          flexDirection: 'column',
          gap: '0.4rem',
          maxHeight: '15rem',
          overflowY: 'scroll',
        }}
        ref={scrollableArea}
      >
        {getCommentsButtonEnabled && selectedComments.length > 0 && (
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.4rem 0.8rem',
              border: '1px solid #e0e0e0',
              borderRadius: '16px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'Lato',
              fontSize: '9pt',
              fontWeight: '500',
              color: '#666',
              marginBottom: '0.5rem',
              alignSelf: 'center',
            }}
            onClick={async () => {
              const request_comment_count = 10;
              const comments_to_add = await peep_client.comments.get_comments({
                timestamp: selectedComments[0].inserted_at.toISOString(),
                direction: 'backwards',
                number: request_comment_count,
                group_id: selectedGroup,
                post_id: postId,
              });

              if (comments_to_add.length < request_comment_count) {
                setGetCommentsButtonEnabled(false);
              }

              addComments(comments_to_add, selectedGroup);
            }}
          >
            load more comments
          </button>
        )}
        {selectedComments.length === 0 && (
          <p
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '2rem 1rem',
              color: '#999',
              fontSize: '10pt',
              fontStyle: 'italic',
              fontFamily: 'Lato',
            }}
          >
            chirp chirp... it's quiet in here
          </p>
        )}
        {selectedComments.map((c) => (
          <div key={c.id.toString()} style={{ display: 'flex', padding: '4px 0' }}>
            <div
              style={{
                height: '24px',
                width: '24px',
                overflow: 'hidden',
                borderRadius: '50%',
                flexShrink: 0,
                marginRight: '8px',
                marginTop: '2px',
              }}
            >
              <img style={{ height: '24px', width: '24px', backgroundColor: 'black', transform: 'scale(1.5)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Commenter relative_to_group_id={c.group_id} user_id={c.commented_by_user_id} />
                {c.commented_by_user_id && user_id === c.commented_by_user_id && !isLoading && (
                  <CommentActions comment_id={c.id} group_id={c.group_id} deleteComment={deleteComment} />
                )}
              </div>
              <RenderedCommentText text={c.text_content || ''} mentions={c.mentions || []} groupId={c.group_id} />
              <ReactionsDisplay
                groupId={c.group_id}
                reactions={c.reactions}
                currentUserId={user_id}
                onAddReaction={(type) => addReactionToComment(c.id, type, selectedGroup)}
                onRemoveReaction={(type) => removeReactionFromComment(c.id, type, selectedGroup)}
              />
            </div>
          </div>
        ))}
      </div>
      <CommentInput
        placeholder="Add to the conversation"
        maxHeight={3}
        groupId={selectedGroup}
        onSubmit={postComment}
      />
    </div>
  );
}

function CommentActions({
  comment_id,
  group_id,
  deleteComment,
}: {
  comment_id: UuidString;
  group_id: UuidString;
  deleteComment: (comment_id: UuidString, group_id: UuidString) => void;
}) {
  return (
    <Menu shadow="md">
      <Menu.Target>
        <Ellipsis size={16} style={{ marginRight: '0.2rem' }} />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => deleteComment(comment_id, group_id)} leftSection={<Trash2 size={12} />}>
          Delete Comment
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function PostActions({
  post_id,
  deletePost,
}: {
  post_id: UuidString;
  deletePost: (post_id: UuidString) => void;
}) {
  return (
    <Menu shadow="md">
      <Menu.Target>
        <Ellipsis size={16} style={{ cursor: 'pointer', color: '#666' }} />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => deletePost(post_id)} leftSection={<Trash2 size={12} />}>
          Delete Post
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function GroupPicker({
  selected_group,
  all_groups,
  onSelect,
}: {
  selected_group: UuidString;
  all_groups: UuidString[];
  onSelect: (newly_selected_group: UuidString) => void;
}) {
  const groups = usePeepStore((state) => state.groups);
  const add_groups = usePeepStore((state) => state.add_groups);
  const abortRef = useAbort();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const alreadyHave = groups
        .filter((cg) => {
          const isStale = isPast(dateAdd(cg.inserted_at, { minutes: 10 }));
          return !isStale;
        })
        .map((cg) => cg.value.id);

      const get = all_groups.filter((g) => !alreadyHave.some((ag) => ag === g));

      if (get.length > 0) {
        const groups = await peep_client.groups.getGroupInfo({ signal: abortRef.current.signal });
        add_groups(groups);
      }

      setLoaded(true);
    })()
      .then()
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const all_groups_sorted = all_groups.sort((a, b) => a.toString().localeCompare(b.toString()));

  if (!loaded) {
    return (
      <span style={{ fontFamily: 'Lato', fontSize: '8pt', color: 'var(--global-gray)', alignSelf: 'center' }}>
        Loading groups...
      </span>
    );
  }

  return (
    <div
      className={'GroupPicker'}
      style={{
        display: 'flex',
        gap: '.3rem',
        marginTop: '0.5rem',
        marginRight: '1rem',
        marginLeft: '1rem',
        justifyContent: 'center',
      }}
    >
      {all_groups_sorted.map((g) => (
        <button
          key={g.toString()}
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '2.2rem',
            width: '5.5rem',
            padding: '0.3rem 0.5rem',
            border: g === selected_group ? '2px solid #4a90e2' : '1px solid #e0e0e0',
            borderRadius: '20px',
            backgroundColor: g === selected_group ? '#f0f7ff' : '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onClick={() => {
            onSelect(g);
          }}
        >
          <div
            style={{
              height: '20px',
              width: '20px',
              overflow: 'hidden',
              borderRadius: '50%',
              flexShrink: 0,
              marginRight: '0.3rem',
            }}
          >
            <img style={{ height: '20px', width: '20px', transform: 'scale(2.0)', backgroundColor: 'black' }} />
          </div>
          <span
            style={{
              fontFamily: 'Lato',
              fontSize: '9pt',
              fontWeight: '500',
              color: g === selected_group ? '#4a90e2' : '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {groups.find((cg) => cg.value.id === g)?.value.display_name}
          </span>
        </button>
      ))}
    </div>
  );
}

function GroupCtx({ selected_group, all_groups }: { selected_group: Uuid; all_groups: Uuid[] }) {
  const groups = usePeepStore((state) => state.groups);
  const add_groups = usePeepStore((state) => state.add_groups);
  const abortRef = useAbort();

  const group_img_size = '10px';

  useEffect(() => {
    (async () => {
      const group_info = groups.find((g) => g.value.id === selected_group);

      if (!group_info || isPast(dateAdd(group_info.inserted_at, { minutes: 10 }))) {
        // no group info (or is stale), grab it
        const new_group_info = await peep_client.groups.getGroupInfo({
          signal: abortRef.current.signal,
        });

        add_groups(new_group_info);
      }
    })()
      .then()
      .catch((err) => console.log(err));
  }, [selected_group]);

  const group_info = groups.find((g) => g.value.id === selected_group)?.value;

  return (
    <div
      className={'GroupCtx'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textWrap: 'nowrap',
        color: 'var(--global-gray-darker-1)',
        fontSize: '10pt',
        marginTop: '0.2rem',
      }}
    >
      <span style={{ fontFamily: 'Lato', marginRight: '.3rem', color: '#666', fontSize: '9pt' }}>
        {group_info ? group_info.display_name : 'Loading'}
      </span>
      <div style={{ height: group_img_size, width: group_img_size, overflow: 'hidden', borderRadius: '50%' }}>
        <img
          style={{ height: group_img_size, width: group_img_size, transform: 'scale(2)', backgroundColor: 'black' }}
        />
      </div>
      {all_groups.length > 1 ? (
        <span style={{ marginLeft: '.2rem', color: '#666', fontSize: '9pt' }}>+{all_groups.length - 1}</span>
      ) : null}
    </div>
  );
}

function MentionedUsers({
  user_ids,
  relative_to_group_id,
  preload_groups,
}: {
  user_ids: UuidString[];
  relative_to_group_id: UuidString;
  preload_groups: UuidString[];
}) {
  const { data: group_members, isLoading } = useGroupMembers(relative_to_group_id);

  for (const gm of preload_groups) {
    preloadGroupMembers(gm);
  }

  if (isLoading) {
    return <p>Loading..</p>;
  }

  const relevent_group_members = user_ids.flatMap((u) => {
    let gm = group_members?.find((gm) => gm.user_id === u);
    return gm ? [gm] : [];
  });

  if (relevent_group_members.length === 0) {
    return (
      <div style={{ display: 'flex', alignSelf: 'center', marginTop: '0.5rem' }}>
        <span style={{ fontFamily: 'Lato', fontSize: '8pt', color: 'var(--global-gray' }}>No mentions</span>
      </div>
    );
  }

  // todo: create a post with mentions to test this out on

  return null;
}

function Poster({ user_id, relative_to_group_id }: { user_id: Uuid; relative_to_group_id: UuidString }) {
  const { data: group_members, isLoading } = useGroupMembers(relative_to_group_id);

  return (
    <span style={{ fontFamily: 'Lato', fontWeight: '600', fontSize: '14pt', color: '#333' }}>
      {isLoading ? 'Loading...' : group_members?.find((gm) => gm.user_id === user_id)?.alias || '??'}
    </span>
  );
}

function Commenter({
  user_id,
  relative_to_group_id,
}: {
  user_id: UuidString | null;
  relative_to_group_id: UuidString;
}) {
  const { data: group_members, isLoading } = useGroupMembers(relative_to_group_id);

  function getAlias() {
    const notFound = '??';
    if (user_id) {
      return group_members?.find((gm) => gm.user_id === user_id)?.alias || notFound;
    }

    return notFound;
  }

  return (
    <div className={'Commenter'}>
      <span style={{ fontFamily: 'Lato', fontWeight: 600, fontSize: '10pt', color: '#333' }}>
        {isLoading ? 'Loading...' : getAlias()}
      </span>
    </div>
  );
}

function RenderedCommentText({
  text,
  mentions,
  groupId,
}: {
  text: string;
  mentions: Comment['mentions'];
  groupId: UuidString;
}) {
  const { data: group_members } = useGroupMembers(groupId);

  if (!text) return null;
  if (!mentions || mentions.length === 0 || !group_members) {
    return (
      <p
        style={{
          fontFamily: 'Lato',
          fontSize: '10pt',
          wordBreak: 'break-word',
          color: '#333',
          lineHeight: '1.3',
          marginTop: '2px',
        }}
      >
        {text}
      </p>
    );
  }

  // Calculate ranges
  const ranges: {
    start: number;
    end: number;
    type: 'mention' | 'legacy' | 'fallback' | 'stripped';
    content?: string;
  }[] = [];

  for (const m of mentions) {
    const charIndex = m.character_index;
    const member = group_members.find((g) => g.user_id === m.mentioned_user_id);
    let matchedLength = 0;
    let type: 'mention' | 'legacy' | 'fallback' | 'stripped' = 'stripped';

    // Strategy 1: Check if this is a "clean" mention (stripped text)
    // Heuristic: If the text at charIndex does NOT start with '@', assume it's stripped.
    // Or if it matches a space/punctuation.

    const isAtChar = text[charIndex] === '@';

    if (!isAtChar) {
      // Likely stripped format
      matchedLength = 0; // It occupies 0 length in the stored text
      type = 'stripped';
    } else {
      // Strategy 2: Check for placeholder @{userId} (Previous format)
      if (m.mentioned_user_id) {
        const placeholder = `@{${m.mentioned_user_id}}`;
        if (text.startsWith(placeholder, charIndex)) {
          matchedLength = placeholder.length;
          type = 'mention';
        }
      }

      // Strategy 3: Legacy strict alias match (backwards compat)
      if (matchedLength === 0 && member) {
        const expected = '@' + member.alias;
        if (text.startsWith(expected, charIndex)) {
          matchedLength = expected.length;
          type = 'legacy';
        }
      }

      // Strategy 4: Fallback (best effort)
      if (matchedLength === 0) {
        const remainingText = text.slice(charIndex);
        const match = remainingText.match(/^@\w+/);
        if (match) {
          matchedLength = match[0].length;
          type = 'fallback';
        }
      }
    }

    if (type === 'stripped' || matchedLength > 0) {
      ranges.push({ start: charIndex, end: charIndex + matchedLength, type, content: member?.alias });
    } else {
      console.warn('Failed to highlight mention at index', charIndex, 'for user', m.mentioned_user_id);
    }
  }

  ranges.sort((a, b) => a.start - b.start);

  const nodes = [];
  let lastIndex = 0;

  ranges.forEach((range, i) => {
    if (range.start < lastIndex) return;

    if (range.start > lastIndex) {
      nodes.push(text.slice(lastIndex, range.start));
    }

    // Render content
    let content: React.ReactNode;

    if (range.type === 'stripped' && range.content) {
      // Insert @Alias
      content = `@${range.content}`;
      // Do NOT advance lastIndex because we didn't consume any text from the string
      lastIndex = range.start;
    } else if (range.type === 'mention' && range.content) {
      content = `@${range.content}`;
      lastIndex = range.end;
    } else {
      content = text.slice(range.start, range.end);
      lastIndex = range.end;
    }

    nodes.push(
      <span key={i} style={{ backgroundColor: '#eebefa', borderRadius: '3px' }}>
        {content}
      </span>,
    );
  });

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return (
    <p
      style={{
        fontFamily: 'Lato',
        fontSize: '10pt',
        wordBreak: 'break-word',
        color: '#333',
        lineHeight: '1.3',
        marginTop: '2px',
      }}
    >
      {nodes}
    </p>
  );
}
